/**
 * "Is this a free email (gmail, outlook, icloud...)?" The list itself lives in the
 * API (mission-x-api/lib/freeEmail.js) - the single source of truth. App.js loads it
 * from GET /api/free-email-domains at start (setFreeEmailList); until it arrives the
 * short built-in patterns below answer. Never add domains here, add them in the API.
 */

// Only for the moment before the API list has loaded (or if it can't be reached)
const FALLBACK_PATTERNS = [
    "@gmail.",
    "@yahoo.",
    "@hotmail",
    "@aol.",
    "@msn.",
    "@live.",
    "@outlook.",
    "@wanadoo.",
    "@free.",
    "@gmx.",
    "@web.",
    "@yandex.",
    "@ymail.",
    "@hey.",
    "@mail.com",
    "@sympatico.ca",
    "@rogers.com",
    "@sharklasers",
    "@guerilla",
    "@spam4",
    "@grr.la",
    "@icloud.com",
    "@proton",
]

// The middle part of a country ending: yahoo.co.uk, hotmail.com.br, yahoo.ne.jp
const COUNTRY_SECOND_LEVELS = new Set(["co", "com", "net", "org", "ne", "or"])

let loaded = null // { domains: Set, providers: Set }

/** Takes the API's list ({ version, providers, domains }). */
export function setFreeEmailList(list) {
    if (!Array.isArray(list?.domains) || !Array.isArray(list?.providers)) return

    loaded = {
        domains: new Set(list.domains.map((d) => String(d).toLowerCase())),
        providers: new Set(list.providers.map((p) => String(p).toLowerCase())),
    }
}

const toDomain = (value) => {
    const text = String(value || "")
        .trim()
        .toLowerCase()
    const at = text.lastIndexOf("@")

    return at === -1 ? text : text.slice(at + 1)
}

/** Same matching as the API's isFreeEmailDomain. */
export function isFreeEmailDomain(domain) {
    const value = toDomain(domain)

    if (!value) return false

    if (!loaded) return FALLBACK_PATTERNS.some((p) => `@${value}`.includes(p))

    if (loaded.domains.has(value)) return true

    const [first, ...rest] = value.split(".")

    if (!loaded.providers.has(first)) return false

    return rest.length === 1 || (rest.length === 2 && COUNTRY_SECOND_LEVELS.has(rest[0]))
}

export function isFreeEmail(email) {
    return isFreeEmailDomain(toDomain(email))
}

/**
 * Can this person email clients as the org (CR sign-off links)? Not from a free
 * email, and not when the org has no company domain of its own (ANY or none) -
 * the API refuses the same (crApproval.service.js#isFreeEmailSender).
 */
export function canEmailAsOrg(senderEmail, inviteDomains) {
    const domains = inviteDomains || []
    const owned = domains.filter((d) => d && d !== "ANY" && !isFreeEmailDomain(d))

    return Boolean(senderEmail) && !isFreeEmail(senderEmail) && !domains.includes("ANY") && owned.length > 0
}
